import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { Send } from "lucide-react";
import { Agent } from "../../model_gen/agent_base";
import { MentionsBox } from "./MentionsBox";
import { Button } from "../ui/button";

interface AssistantInputAreaProps {
  availableAgents: Agent[];
  currentMentions: Agent[];
  onSendMessage: (message: string, mentions: Agent[]) => void;
  onUpdateMentions: (mentions: Agent[]) => void;
  isLoading?: boolean;
}

export interface AssistantInputAreaRef {
  removeMentionFromText: (agent: Agent) => void;
}

export const AssistantInputArea = forwardRef<AssistantInputAreaRef, AssistantInputAreaProps>(({
  availableAgents,
  currentMentions,
  onSendMessage,
  onUpdateMentions,
  isLoading = false
}, ref) => {
  const [message, setMessage] = useState("");
  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLDivElement>(null);
  const isSendingRef = useRef(false);
  
  // Helper function to convert message to display format with AGENT[Name] markers
  const convertToMarkerFormat = (text: string): string => {
    // Convert @Agent Name to AGENT[Agent Name]
    // Match @ followed by any characters until we hit a space + lowercase letter, or end of string
    let result = text;
    
    // Find all mentions in the current context and replace them
    currentMentions.forEach(agent => {
      const mentionPattern = new RegExp(`@${agent.agent_name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\s|$)`, "g");
      result = result.replace(mentionPattern, `AGENT[${agent.agent_name}]`);
    });
    
    return result;
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    removeMentionFromText: (agent: Agent) => {
      if (!textAreaRef.current) return;
      
      let currentText = message;
      const mentionText = `@${agent.agent_name}`;
      
      // Remove ALL occurrences of the mention
      while (currentText.includes(`${mentionText} `)) {
        currentText = currentText.replace(`${mentionText} `, "");
      }
      while (currentText.includes(mentionText)) {
        currentText = currentText.replace(mentionText, "");
      }
      
      setMessage(currentText);
      
      // Remove from context since we're removing all occurrences
      const updatedMentions = currentMentions.filter(a => a.id !== agent.id);
      const styledContent = renderStyledMessage(currentText, updatedMentions);
      textAreaRef.current.innerHTML = styledContent || "<br>";
    }
  }));
  
  // Helper function to get cursor position in contenteditable
  const getCursorPosition = (element: HTMLElement): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;
    
    const range = selection.getRangeAt(0);
    const clonedRange = range.cloneRange();
    clonedRange.selectNodeContents(element);
    clonedRange.setEnd(range.endContainer, range.endOffset);
    return clonedRange.toString().length;
  };

  // Helper function to set cursor position in contenteditable
  const setCursorAtPosition = (element: HTMLElement, position: number) => {
    const selection = window.getSelection();
    if (!selection) return;
    
    const range = document.createRange();
    let currentPos = 0;
    let foundNode = false;
    
    const walkTextNodes = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent?.length || 0;
        if (currentPos + textLength >= position) {
          range.setStart(node, position - currentPos);
          range.collapse(true);
          foundNode = true;
          return true;
        }
        currentPos += textLength;
      } else if (node.childNodes) {
        for (let i = 0; i < node.childNodes.length; i++) {
          if (walkTextNodes(node.childNodes[i])) return true;
        }
      }
      return false;
    };
    
    walkTextNodes(element);
    
    if (!foundNode) {
      range.selectNodeContents(element);
      range.collapse(false);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
  };
  
  // Initialize contenteditable with br on mount
  useEffect(() => {
    if (textAreaRef.current && !textAreaRef.current.innerHTML) {
      textAreaRef.current.innerHTML = "<br>";
    }
  }, []);


  // Handle click outside to close mention popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowMentionPopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderStyledMessage = (text: string, mentions: Agent[]) => {
    if (!text) return "";
    
    // Escape HTML characters to prevent them from being interpreted as tags
    const escapeHtml = (str: string) => {
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    };
    
    let styledText = escapeHtml(text);
    
    // Convert newlines to <br> tags
    styledText = styledText.replace(/\n/g, "<br>");
    
    mentions.forEach(agent => {
      // Escape the agent name for use in regex
      const escapedAgentName = agent.agent_name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      // Match the mention and capture any trailing space
      const regex = new RegExp(`(@${escapedAgentName})(\\s)?`, "g");
      styledText = styledText.replace(regex, (match, mention, space) => {
        // Preserve the space after the mention using a non-breaking space
        const spaceAfter = space ? "\u00A0" : "";
        return `<span style="color: #10b981; font-weight: 600;">${escapeHtml(mention)}</span>${spaceAfter}`;
      });
    });
    
    return styledText;
  };

  const handleSend = () => {
    if (message.trim() && !isLoading) {
      // Convert @mentions to AGENT[Name] format before sending
      const formattedMessage = convertToMarkerFormat(message);
      onSendMessage(formattedMessage, currentMentions);
      
      // Clear the input field
      // The parent (AssistantSidebar) will handle accumulating mentions in context
      setMessage("");
      if (textAreaRef.current) {
        textAreaRef.current.innerHTML = "<br>";
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLDivElement;
    const selection = window.getSelection();
    
    // Handle space key when cursor is at the start of a mention
    if (e.key === " " && selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const node = range.startContainer;
      const offset = range.startOffset;
      
      // Check if we're at the end of a text node and the next sibling is a mention
      if (range.collapsed && node.nodeType === Node.TEXT_NODE && 
          offset === node.textContent?.length &&
          node.nextSibling && 
          node.nextSibling.nodeType === Node.ELEMENT_NODE &&
          (node.nextSibling as HTMLElement).tagName === "SPAN" &&
          (node.nextSibling as HTMLElement).style.color === "rgb(16, 185, 129)") {
        // Let the default behavior happen - insert space before the mention
        return;
      }
      
      // Check if the immediate next node is a mention span (when cursor is in an empty text node)
      if (range.collapsed && node.nextSibling && 
          node.nextSibling.nodeType === Node.ELEMENT_NODE &&
          (node.nextSibling as HTMLElement).tagName === "SPAN" &&
          (node.nextSibling as HTMLElement).style.color === "rgb(16, 185, 129)") {
        // Let the default behavior happen - insert space before the mention
        return;
      }
    }
    
    if (showMentionPopup && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      if (e.key === "ArrowUp") {
        setSelectedIndex(prev => Math.max(0, prev - 1));
      } else {
        setSelectedIndex(prev => prev + 1);
      }
      return;
    }
    
    if (showMentionPopup && (e.key === "Enter" || e.key === "Tab") && !e.shiftKey) {
      // Get filtered agents to select the right one
      const filteredAgents = availableAgents.filter(agent => 
        agent.agent_name.toLowerCase().includes(mentionQuery.toLowerCase())
      );
      if (filteredAgents.length > 0 && filteredAgents[selectedIndex]) {
        e.preventDefault();
        handleAgentSelect(filteredAgents[selectedIndex]);
        return;
      }
      // If no agents to select, fall through to send message (for Enter) or default Tab behavior
      if (e.key === "Tab") {
        return; // Let Tab do its default behavior if no agents
      }
      // Fall through to send message logic below for Enter key
    }
    
    // Allow Shift+Enter to create new lines
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      // Insert a line break manually
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        // Insert two <br> tags to ensure proper line breaking behavior
        const br1 = document.createElement("br");
        const br2 = document.createElement("br");
        range.insertNode(br1);
        range.insertNode(br2);
        
        // Position cursor after the first br (between the two)
        range.setStartAfter(br1);
        range.setEndAfter(br1);
        range.collapse(false);
        
        selection.removeAllRanges();
        selection.addRange(range);
      }
      return;
    }
    
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    // Handle backspace/delete for mentions
    if ((e.key === "Backspace" || e.key === "Delete") && selection && selection.rangeCount > 0) {
      // Get current text and check if we need to delete a mention
      const currentText = message || "";
      const cursorPos = getCursorPosition(target);
      
      // For backspace, check if there's a mention ending right before cursor
      if (e.key === "Backspace" && cursorPos > 0) {
        // Check each mention to see if we're at the end of it
        for (const agent of currentMentions) {
          const mentionText = `@${agent.agent_name}`;
          
          // Find all occurrences of this mention
          let searchIndex = 0;
          while (searchIndex < currentText.length) {
            const mentionIndex = currentText.indexOf(mentionText, searchIndex);
            if (mentionIndex === -1) break;
            
            const mentionEnd = mentionIndex + mentionText.length;
            
            // Check if cursor is at the end of this mention (or right after a space following it)
            // Also check if we're within the mention (for when cursor is inside the styled span)
            if ((cursorPos === mentionEnd) || 
                (cursorPos === mentionEnd + 1 && currentText[mentionEnd] === " ") ||
                (cursorPos > mentionIndex && cursorPos <= mentionEnd)) {
              e.preventDefault();
              
              // Remove the mention text from the full text
              let newText = currentText.substring(0, mentionIndex) + 
                           currentText.substring(mentionEnd);
              
              // Also remove trailing space if cursor was after it
              if (cursorPos === mentionEnd + 1 && newText[mentionIndex] === " ") {
                newText = newText.substring(0, mentionIndex) + newText.substring(mentionIndex + 1);
              }
              
              // Check if there are still other occurrences of this mention
              const stillHasMentions = newText.includes(`@${agent.agent_name}`);
              
              // Only remove from currentMentions if there are NO more occurrences
              const updatedMentions = stillHasMentions 
                ? currentMentions 
                : currentMentions.filter(a => a.id !== agent.id);
              onUpdateMentions(updatedMentions);
              
              // Update state and re-render
              setMessage(newText);
              const styledContent = renderStyledMessage(newText, updatedMentions);
              target.innerHTML = styledContent || "<br>";
              
              // Close mention popup if it's open
              setShowMentionPopup(false);
              setMentionQuery("");
              
              // Set cursor position where the mention was
              setCursorAtPosition(target, mentionIndex);
              return;
            }
            
            searchIndex = mentionEnd;
          }
        }
      }
    }

    // Handle arrow key navigation around mentions
    if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const node = range.startContainer;
      const offset = range.startOffset;
      const parentElement = node.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement;
      
      // If cursor is inside a mention, jump to the boundary
      if (parentElement && parentElement.tagName === "SPAN" && parentElement.style.color === "rgb(16, 185, 129)") {
        e.preventDefault();
        const newRange = document.createRange();
        
        if (e.key === "ArrowLeft") {
          // Jump to before the @ symbol
          newRange.setStartBefore(parentElement);
        } else {
          // Jump to after the mention (and any trailing space)
          if (parentElement.nextSibling && parentElement.nextSibling.nodeType === Node.TEXT_NODE && 
              parentElement.nextSibling.textContent?.startsWith("\u00A0")) {
            newRange.setStart(parentElement.nextSibling, 1);
          } else {
            newRange.setStartAfter(parentElement);
          }
        }
        
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
        return;
      }
      
      // Check if we're at the edge of a text node next to a mention
      if (node.nodeType === Node.TEXT_NODE) {
        // Moving right and at the end of text node, check if next is mention
        if (e.key === "ArrowRight" && offset === node.textContent?.length && 
            node.nextSibling && node.nextSibling.nodeType === Node.ELEMENT_NODE &&
            (node.nextSibling as HTMLElement).tagName === "SPAN" &&
            (node.nextSibling as HTMLElement).style.color === "rgb(16, 185, 129)") {
          e.preventDefault();
          const mention = node.nextSibling as HTMLElement;
          const newRange = document.createRange();
          
          // Jump to after the mention (and any trailing space)
          if (mention.nextSibling && mention.nextSibling.nodeType === Node.TEXT_NODE && 
              mention.nextSibling.textContent?.startsWith("\u00A0")) {
            newRange.setStart(mention.nextSibling, 1);
          } else {
            newRange.setStartAfter(mention);
          }
          
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          return;
        }
        
        // Moving left and at the start of text node, check if previous is mention
        if (e.key === "ArrowLeft" && offset === 0 && 
            node.previousSibling && node.previousSibling.nodeType === Node.ELEMENT_NODE &&
            (node.previousSibling as HTMLElement).tagName === "SPAN" &&
            (node.previousSibling as HTMLElement).style.color === "rgb(16, 185, 129)") {
          e.preventDefault();
          const mention = node.previousSibling as HTMLElement;
          const newRange = document.createRange();
          
          // Jump to before the @ symbol
          newRange.setStartBefore(mention);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          return;
        }
      }
    }
  };

  const handleAgentSelect = (agent: Agent) => {
    if (textAreaRef.current) {
      const text = message || "";
      
      // Find the @ symbol position
      let atPosition = -1;
      for (let i = cursorPosition - 1; i >= 0; i--) {
        if (text[i] === "@") {
          atPosition = i;
          break;
        }
      }
      
      if (atPosition !== -1) {
        // Replace from @ to cursor position with the agent mention
        const beforeMention = text.substring(0, atPosition);
        const afterMention = text.substring(cursorPosition);
        const newText = `${beforeMention}@${agent.agent_name} ${afterMention}`;
        
        // Update currentMentions - add even if already present (allow duplicates)
        // But only add to the list if not already there (for Context display)
        const alreadyInContext = currentMentions.some(a => a.id === agent.id);
        const updatedMentions = alreadyInContext ? currentMentions : [...currentMentions, agent];
        onUpdateMentions(updatedMentions);
        
        // Update the contenteditable with styled content
        const styledContent = renderStyledMessage(newText, updatedMentions);
        textAreaRef.current.innerHTML = styledContent || "<br>";
        
        // Set cursor after the mention
        const selection = window.getSelection();
        if (selection) {
          // Find the position after the agent name
          const mentionLength = agent.agent_name.length + 2; // +2 for @ and space
          const newCursorPos = atPosition + mentionLength;
          
          // Move cursor to end for simplicity
          const range = document.createRange();
          range.selectNodeContents(textAreaRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        setMessage(newText);
        setShowMentionPopup(false);
        setMentionQuery("");
        
        // Focus back on the input
        textAreaRef.current.focus();
      }
    }
  };

  return (
    <div className="p-4 border-t border-border bg-gradient-to-t from-background to-background/95">
      <div className="relative" ref={wrapperRef}>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            {/* Custom input with contenteditable */}
            <div className="relative">
              {/* Placeholder */}
              {!message.trim() && (
                <div 
                  className="absolute top-3 left-3 pointer-events-none" 
                  style={{ 
                    fontSize: "14px",
                    lineHeight: "21px",
                    fontFamily: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif",
                    color: "#9ca3af",
                    zIndex: 10
                  }}
                >
                  Type @ to mention an agent...
                </div>
              )}
              
              <div
                ref={textAreaRef}
                contentEditable
                className="min-h-[80px] max-h-[160px] overflow-y-auto p-3 pr-12 rounded-lg border border-border/50 focus:border-maitai-green/50 transition-colors outline-none bg-background relative"
                style={{
                  fontSize: "14px",
                  lineHeight: "21px",
                  wordBreak: "break-word",
                  fontFamily: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif",
                  letterSpacing: "normal",
                  zIndex: 2,
                  caretColor: "currentColor"
                }}
                onInput={(e) => {
                  const target = e.target as HTMLDivElement;
                  
                  // Get text content, preserving special characters like <
                  let text = "";
                  const walker = document.createTreeWalker(
                    target,
                    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
                    {
                      acceptNode: (node) => {
                        if (node.nodeType === Node.TEXT_NODE) {
                          return NodeFilter.FILTER_ACCEPT;
                        }
                        if (node.nodeType === Node.ELEMENT_NODE) {
                          const element = node as HTMLElement;
                          // Skip mention spans
                          if (element.tagName === "SPAN" && element.className.includes("mention")) {
                            return NodeFilter.FILTER_REJECT;
                          }
                          // Convert BR to newline
                          if (element.tagName === "BR") {
                            return NodeFilter.FILTER_ACCEPT;
                          }
                        }
                        return NodeFilter.FILTER_SKIP;
                      }
                    }
                  );
                  
                  let node;
                  while (node = walker.nextNode()) {
                    if (node.nodeType === Node.TEXT_NODE) {
                      text += node.textContent || "";
                    } else if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === "BR") {
                      text += "\n";
                    }
                  }
                  
                  // Handle empty state with just br tag
                  if (target.innerHTML === "<br>" || target.innerHTML === "<br/>") {
                    text = "";
                  }
                  
                  // Prevent contenteditable from collapsing when empty
                  if (!text && !target.innerHTML) {
                    target.innerHTML = "<br>";
                  }
                  
                  setMessage(text);
                  
                  // Get more accurate cursor position
                  const selection = window.getSelection();
                  let cursorPos = 0;
                  if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    const clonedRange = range.cloneRange();
                    clonedRange.selectNodeContents(target);
                    clonedRange.setEnd(range.endContainer, range.endOffset);
                    cursorPos = clonedRange.toString().length;
                  }
                  setCursorPosition(cursorPos);
                  
                  // Check for @ symbol
                  const lastAtIndex = text.lastIndexOf("@", cursorPos);
                  const hasSpaceAfterAt = lastAtIndex !== -1 && lastAtIndex < text.length - 1 && text[lastAtIndex + 1] === " ";
                  
                  // Check if the @ is part of an existing complete mention
                  let isInCompleteMention = false;
                  if (lastAtIndex !== -1) {
                    for (const agent of currentMentions) {
                      const mentionText = `@${agent.agent_name}`;
                      const mentionIndex = text.indexOf(mentionText, Math.max(0, cursorPos - mentionText.length - 10));
                      if (mentionIndex !== -1 && mentionIndex === lastAtIndex && 
                          cursorPos > lastAtIndex + mentionText.length) {
                        // Cursor is after a complete mention
                        isInCompleteMention = true;
                        break;
                      }
                    }
                  }
                  
                  if (lastAtIndex !== -1 && !hasSpaceAfterAt && !isInCompleteMention && cursorPos > lastAtIndex) {
                    const query = text.substring(lastAtIndex + 1, cursorPos);
                    setMentionQuery(query);
                    setShowMentionPopup(true);
                    setSelectedIndex(0);
                  } else {
                    setShowMentionPopup(false);
                    setMentionQuery("");
                  }
                  
                  // Apply styling to existing mentions
                  const styledContent = renderStyledMessage(text, currentMentions);
                  if (styledContent !== target.innerHTML && styledContent) {
                    // Store current cursor position before updating HTML
                    const currentCursorPos = cursorPos;
                    
                    target.innerHTML = styledContent;
                    
                    // Restore cursor to its original position
                    setCursorAtPosition(target, currentCursorPos);
                  }
                  
                  // Note: We don't automatically remove mentions from context when they're deleted from text
                  // The user must explicitly remove them via the X button in the Context section
                }}
                onKeyDown={onKeyDown}
                onMouseDown={(e) => {
                  const target = e.target as HTMLElement;
                  
                  // Check if we're clicking on a mention span
                  if (target.tagName === "SPAN" && target.style.color === "rgb(16, 185, 129)") {
                    e.preventDefault();
                    
                    // Move cursor after the mention and its trailing space
                    setTimeout(() => {
                      const selection = window.getSelection();
                      if (selection) {
                        const range = document.createRange();
                        // Check if there's a text node after the span (the space)
                        if (target.nextSibling && target.nextSibling.nodeType === Node.TEXT_NODE) {
                          range.setStart(target.nextSibling, 1); // Position after the space
                        } else {
                          range.setStartAfter(target);
                        }
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                      }
                    }, 0);
                  }
                }}
              />
            </div>
          </div>
          <Button
            onClick={handleSend}
            disabled={!message.trim() || isLoading}
            className="self-end bg-maitai-green hover:bg-maitai-green/90 text-white rounded-lg px-3 py-2 h-10"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>

        {/* Mention popup */}
        {showMentionPopup && (
          <MentionsBox
            availableAgents={availableAgents}
            mentionQuery={mentionQuery}
            selectedIndex={selectedIndex}
            onSelect={handleAgentSelect}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
});
